import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import gsap from 'gsap';
import { ErrorState } from '../components/ui';

// Map POS product category to nasi_dus item_type
function mapProductCategory(product) {
  if (product.is_drink) return 'drink';
  if (product.is_packet) return 'packaging';
  const cat = (product.category || '').toLowerCase();
  const map = { nasi: 'rice', rice: 'rice', ayam: 'main_dish', chicken: 'main_dish', ikan: 'main_dish', fish: 'main_dish', daging: 'main_dish', meat: 'main_dish', sayur: 'side_dish', vegetable: 'side_dish', tahu: 'side_dish', tempe: 'side_dish', sambal: 'sambal', sauce: 'sambal', drink: 'drink', beverage: 'drink', minuman: 'drink' };
  for (const [key, val] of Object.entries(map)) {
    if (cat.includes(key)) return val;
  }
  return 'extra';
}

// Get localized field with fallback
const getLocalizedField = (item, field, lang) => {
  const langCode = lang === 'id' ? 'id' : lang === 'ar' ? 'ar' : lang === 'ms' ? 'ms' : 'en';
  return item?.[`${field}_${langCode}`] || item?.[`${field}_en`] || item?.[field] || '';
};

// Format price
const formatPrice = (price, currency = 'IDR') => {
  if (!price) return '-';
  const formatters = {
    IDR: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }),
    SGD: new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }),
    MYR: new Intl.NumberFormat('ms-MY', { style: 'currency', currency: 'MYR' }),
    SAR: new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR' }),
    AUD: new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }),
    USD: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
  };
  return formatters[currency]?.format(price) || `${currency} ${price}`;
};

// Haversine distance in km
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Get tomorrow's date as min for date picker
const getTomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

// Status badge colors
const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-indigo-100 text-indigo-800',
  ready: 'bg-green-100 text-green-800',
  delivered: 'bg-green-200 text-green-900',
  completed: 'bg-green-200 text-green-900',
  cancelled: 'bg-red-100 text-red-800',
};

// Animated scroll-reveal wrapper
function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    gsap.set(ref.current, { opacity: 0, y: 40 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(ref.current, {
              opacity: 1,
              y: 0,
              duration: 0.7,
              delay,
              ease: 'power3.out',
            });
            observer.unobserve(ref.current);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

// Animated package card
function AnimatedPackageCard({ pkg, lang, onSelect, isSelected, t, index }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (!cardRef.current) return;
    gsap.set(cardRef.current, { opacity: 0, y: 50, scale: 0.95 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(cardRef.current, {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.6,
              delay: (index % 3) * 0.1,
              ease: 'power3.out',
            });
            observer.unobserve(cardRef.current);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index]);

  const name = getLocalizedField(pkg, 'name', lang);
  const description = getLocalizedField(pkg, 'description', lang);
  const menuItems = pkg.menu_items || [];

  return (
    <div
      ref={cardRef}
      className={`bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col cursor-pointer ${isSelected ? 'ring-2 ring-primary-500 shadow-lg' : ''}`}
      onClick={() => onSelect(pkg)}
    >
      {/* Image */}
      <div className="aspect-[16/10] bg-gray-100 relative overflow-hidden">
        {pkg.image_url ? (
          <img src={pkg.image_url} alt={name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
            <svg className="w-16 h-16 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        )}
        {isSelected && (
          <div className="absolute top-3 right-3 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-bold text-xl text-gray-900 mb-2">{name}</h3>
        {description && <p className="text-gray-600 text-sm mb-4 line-clamp-2">{description}</p>}

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-2xl font-bold text-primary-600">{formatPrice(pkg.price_per_box)}</span>
          <span className="text-gray-500 text-sm">/{t('nasiDus.perBox')}</span>
        </div>

        {/* Menu Items */}
        {menuItems.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('nasiDus.menuIncludes')}</p>
            <ul className="text-sm text-gray-600 space-y-1">
              {menuItems.slice(0, 6).map((item, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0"></span>
                  {getLocalizedField(item, 'name', lang)}
                </li>
              ))}
              {menuItems.length > 6 && (
                <li className="text-primary-600 text-sm">+{menuItems.length - 6} more</li>
              )}
            </ul>
          </div>
        )}

        <div className="flex-1"></div>

        {/* CTA */}
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(pkg); }}
          className={`w-full py-3 font-medium rounded-lg transition-colors ${isSelected ? 'bg-primary-700 text-white' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
        >
          {isSelected ? t('nasiDus.selected') : t('nasiDus.selectPackage')}
        </button>
      </div>
    </div>
  );
}

// Menu Item Tile
function MenuItemTile({ item, lang, quantity, onAdd, onRemove }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${quantity > 0 ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
      {item.image_url ? (
        <img src={item.image_url} alt={getLocalizedField(item, 'name', lang)} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-gray-900 truncate">{getLocalizedField(item, 'name', lang)}</p>
          {item._source === 'pos' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 flex-shrink-0">POS</span>
          )}
        </div>
        <p className="text-xs text-gray-500">{formatPrice(item.price)}</p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {quantity > 0 && (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="w-7 h-7 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-300 transition-colors text-sm font-bold"
          >
            -
          </button>
        )}
        {quantity > 0 && (
          <span className="w-6 text-center text-sm font-medium text-primary-700">{quantity}</span>
        )}
        <button
          type="button"
          onClick={() => onAdd(item.id)}
          className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 transition-colors text-sm font-bold"
        >
          +
        </button>
      </div>
    </div>
  );
}

// FAQ Accordion Item
function FaqItem({ question, answer, isOpen, onToggle }) {
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;
    if (isOpen) {
      gsap.to(contentRef.current, { height: 'auto', opacity: 1, duration: 0.3, ease: 'power2.out' });
    } else {
      gsap.to(contentRef.current, { height: 0, opacity: 0, duration: 0.25, ease: 'power2.in' });
    }
  }, [isOpen]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        <svg
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div ref={contentRef} className="overflow-hidden" style={{ height: 0, opacity: 0 }}>
        <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">{answer}</div>
      </div>
    </div>
  );
}

// Order Card for My Orders
function OrderCard({ order, t }) {
  const status = order.status || 'draft';
  const colorClass = statusColors[status] || statusColors.draft;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-gray-900">{order.order_number}</p>
          <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>
      <div className="space-y-1.5 text-sm text-gray-600">
        {order.scheduled_date && (
          <div className="flex justify-between">
            <span>{t('nasiDus.scheduledDate')}</span>
            <span className="font-medium text-gray-900">{new Date(order.scheduled_date).toLocaleDateString()}</span>
          </div>
        )}
        {order.total_amount > 0 && (
          <div className="flex justify-between">
            <span>{t('nasiDus.total')}</span>
            <span className="font-medium text-gray-900">{formatPrice(order.total_amount)}</span>
          </div>
        )}
        {order.deposit_required > 0 && (
          <div className="flex justify-between">
            <span>{t('nasiDus.depositAmount')}</span>
            <span className="font-medium text-primary-700">{formatPrice(order.deposit_required)}</span>
          </div>
        )}
        {order.deposit_paid > 0 && (
          <div className="flex justify-between">
            <span>{t('nasiDus.depositPaid')}</span>
            <span className="font-medium text-green-700">{formatPrice(order.deposit_paid)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Page
export default function NasiDus() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [packages, setPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [packageItems, setPackageItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [user, setUser] = useState(null);
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  // Box builder state: { [menuItemId]: quantity }
  const [boxItems, setBoxItems] = useState({});
  const [selectedPackageId, setSelectedPackageId] = useState(null);

  const headerRef = useRef(null);
  const formRef = useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      fulfillment_type: 'pickup',
      quantity: 10,
      is_custom: false,
    },
  });

  const watchBranch = watch('branch_id');
  const watchFulfillment = watch('fulfillment_type');
  const watchIsCustom = watch('is_custom');
  const watchQuantity = watch('quantity');

  // Compute box cost from selected items
  const boxCost = Object.entries(boxItems).reduce((sum, [itemId, qty]) => {
    const item = menuItems.find((m) => m.id === itemId);
    return sum + (item?.price || 0) * qty;
  }, 0);
  const quantity = parseInt(watchQuantity) || 0;
  const subtotal = boxCost * quantity;
  const depositRequired = Math.ceil(subtotal * 0.5);

  // Add item to box
  const addToBox = useCallback((itemId) => {
    setBoxItems((prev) => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
  }, []);

  // Remove item from box
  const removeFromBox = useCallback((itemId) => {
    setBoxItems((prev) => {
      const next = { ...prev };
      if (next[itemId] > 1) {
        next[itemId] -= 1;
      } else {
        delete next[itemId];
      }
      return next;
    });
  }, []);

  // Select a package preset - fills box with its items
  const selectPackagePreset = useCallback((pkg) => {
    setSelectedPackageId(pkg.id);
    // Find items for this package
    const items = packageItems.filter((pi) => pi.package_id === pkg.id);
    const newBox = {};
    items.forEach((pi) => {
      if (pi.nasi_dus_menu_items?.id) {
        newBox[pi.nasi_dus_menu_items.id] = pi.quantity || 1;
      } else if (pi.menu_item_id) {
        newBox[pi.menu_item_id] = pi.quantity || 1;
      }
    });
    setBoxItems(newBox);
    setValue('package_id', pkg.id);
    if (pkg.branch_id) setValue('branch_id', pkg.branch_id);
    setValue('is_custom', false);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [packageItems, setValue]);

  // Fetch auth user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      setUser(authUser);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const [pkgRes, branchRes, menuRes, pkgItemsRes, productsRes] = await Promise.all([
          supabase
            .from('nasi_dus_packages')
            .select('*')
            .eq('is_active', true)
            .order('sort_order'),
          // select * so it keeps working before migration 20261146 adds accepts_orders
          supabase
            .from('branches')
            .select('*')
            .order('name'),
          supabase
            .from('nasi_dus_menu_items')
            .select('*')
            .eq('is_active', true)
            .order('sort_order'),
          supabase
            .from('nasi_dus_package_items')
            .select('*, nasi_dus_menu_items(*)')
            .order('sort_order'),
          supabase
            .from('products')
            .select('id, name, default_price, category, product_picture_url, branch_id, is_drink, is_packet, is_frozen'),
        ]);

        if (pkgRes.error) throw pkgRes.error;
        if (branchRes.error) throw branchRes.error;
        if (menuRes.error) throw menuRes.error;
        if (pkgItemsRes.error) throw pkgItemsRes.error;

        // Merge nasi_dus_menu_items with POS products, deduplicating by name
        const nasiDusItems = menuRes.data || [];
        const nasiDusNames = new Set(nasiDusItems.map((m) => m.name.toLowerCase()));
        const posProducts = (productsRes.data || [])
          .filter((p) => !nasiDusNames.has(p.name.toLowerCase()))
          .map((p) => ({
            id: p.id,
            branch_id: p.branch_id,
            name: p.name,
            name_en: null,
            name_ar: null,
            name_ms: null,
            item_type: mapProductCategory(p),
            price: p.default_price,
            image_url: p.product_picture_url,
            description: null,
            is_active: true,
            sort_order: 999,
            _source: 'pos',
          }));

        setPackages(pkgRes.data || []);
        // only outlets that take orders (run the Ampera POS) — the others would never see the order
        setBranches((branchRes.data || [])
          .filter((b) => b.accepts_orders !== false && !b.is_stock_center)
          .map(({ branch_id, name, lat, lng }) => ({ branch_id, name, lat, lng })));
        setMenuItems([...nasiDusItems, ...posProducts]);
        setPackageItems(pkgItemsRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Geolocation - auto-suggest nearest branch
  useEffect(() => {
    if (branches.length === 0) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });

        const nearest = branches
          .filter((b) => b.lat && b.lng)
          .map((b) => ({
            ...b,
            distance: haversine(latitude, longitude, parseFloat(b.lat), parseFloat(b.lng)),
          }))
          .sort((a, b) => a.distance - b.distance);

        if (nearest.length > 0) {
          setValue('branch_id', nearest[0].branch_id);
        }
      },
      () => {} // Silently fail if denied
    );
  }, [branches, setValue]);

  // Sort branches by distance when location available
  const sortedBranches = userLocation
    ? [...branches]
        .map((b) => ({
          ...b,
          distance: b.lat && b.lng
            ? haversine(userLocation.latitude, userLocation.longitude, parseFloat(b.lat), parseFloat(b.lng))
            : Infinity,
        }))
        .sort((a, b) => a.distance - b.distance)
    : branches;

  // Fetch my orders
  useEffect(() => {
    if (!user) {
      setMyOrders([]);
      return;
    }

    const fetchOrders = async () => {
      setOrdersLoading(true);
      const { data, error } = await supabase
        .from('nasi_dus_orders')
        .select('*')
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false });

      if (!error) setMyOrders(data || []);
      setOrdersLoading(false);
    };
    fetchOrders();

    // Real-time subscription
    const channel = supabase
      .channel('nasi_dus_orders_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'nasi_dus_orders', filter: `customer_id=eq.${user.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setMyOrders((prev) => [payload.new, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setMyOrders((prev) => prev.map((o) => (o.id === payload.new.id ? payload.new : o)));
          } else if (payload.eventType === 'DELETE') {
            setMyOrders((prev) => prev.filter((o) => o.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Animate header on mount
  useEffect(() => {
    if (!loading && headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.2, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Group menu items by item_type
  const menuGroups = menuItems.reduce((acc, item) => {
    const type = item.item_type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  // Submit order
  const onSubmit = async (data) => {
    try {
      const calcSubtotal = watchIsCustom ? 0 : boxCost * (parseInt(data.quantity) || 0);
      const calcDeposit = Math.ceil(calcSubtotal * 0.5);

      const orderPayload = {
        branch_id: data.branch_id || null,
        customer_name: data.customer_name,
        customer_phone: data.customer_phone,
        customer_email: data.customer_email || null,
        customer_company: data.customer_company || null,
        customer_id: user?.id || null,
        event_name: data.event_name || null,
        is_custom: data.is_custom || false,
        package_id: data.is_custom ? null : data.package_id || null,
        quantity_boxes: parseInt(data.quantity) || 0,
        price_per_box: data.is_custom ? 0 : boxCost,
        subtotal: calcSubtotal,
        discount_percent: 0,
        discount_amount: 0,
        total_amount: calcSubtotal,
        deposit_percent: 50,
        deposit_required: calcDeposit,
        fulfillment_type: data.fulfillment_type,
        scheduled_date: data.scheduled_date,
        scheduled_time: data.scheduled_time || null,
        delivery_address: data.fulfillment_type === 'delivery' ? data.delivery_address : null,
        notes: data.notes || null,
        special_requests: data.special_requests || null,
        status: 'draft',
        source: 'website',
      };

      const { data: inserted, error: insertError } = await supabase
        .from('nasi_dus_orders')
        .insert(orderPayload)
        .select('id, order_number')
        .single();
      if (insertError) throw insertError;

      // Save order items with menu_item_id references
      if (!watchIsCustom && Object.keys(boxItems).length > 0 && inserted?.id) {
        const orderItemRows = Object.entries(boxItems).map(([menuItemId, qty], i) => {
          const mi = menuItems.find((m) => m.id === menuItemId);
          const isPosItem = mi?._source === 'pos';
          return {
            order_id: inserted.id,
            menu_item_id: isPosItem ? null : menuItemId,
            item_name: mi?.name || null,
            item_type: 'extra',
            quantity: qty,
            price: mi?.price || 0,
            sort_order: i,
          };
        });

        const { error: itemsError } = await supabase
          .from('nasi_dus_order_items')
          .insert(orderItemRows);
        // Items are the order — if they fail the order is meaningless, so
        // surface it rather than delivering an empty order to the kitchen.
        if (itemsError) throw itemsError;
      }

      setOrderSuccess({
        orderNumber: inserted?.order_number || 'Submitted',
        deposit: formatPrice(calcDeposit),
      });
      reset();
      setBoxItems({});
      setSelectedPackageId(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Order submit error:', err);
      alert('Failed to submit order. Please try again.');
    }
  };

  // Filtered packages by selected branch (show all if no branch selected)
  const filteredPackages = watchBranch
    ? packages.filter((p) => !p.branch_id || p.branch_id === watchBranch)
    : packages;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
          <span className="text-gray-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="py-16 md:py-24">
        <div className="max-w-lg mx-auto px-4">
          <ErrorState onRetry={fetchData} />
        </div>
      </div>
    );
  }

  // Success state
  if (orderSuccess) {
    return (
      <div className="py-16 md:py-24">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('nasiDus.orderSuccess')}</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {t('nasiDus.orderSuccessMsg', {
              orderNumber: orderSuccess.orderNumber,
              deposit: orderSuccess.deposit,
            })}
          </p>
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-8">
            <p className="text-sm text-primary-700 font-medium">{t('nasiDus.depositAmount')}</p>
            <p className="text-2xl font-bold text-primary-600 mt-1">{orderSuccess.deposit}</p>
          </div>
          <button
            onClick={() => setOrderSuccess(null)}
            className="px-8 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('nasiDus.orderNow')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ===== HERO SECTION ===== */}
        <div ref={headerRef} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            {t('nasiDus.title')}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('nasiDus.subtitle')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            {t('nasiDus.heroText')}
          </p>
          <button
            onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {t('nasiDus.orderNow')}
          </button>
        </div>

        {/* ===== HOW IT WORKS ===== */}
        <AnimatedSection className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
            {t('nasiDus.howItWorks')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: 1, icon: (
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ), title: t('nasiDus.step1'), desc: t('nasiDus.step1Desc') },
              { step: 2, icon: (
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              ), title: t('nasiDus.step2'), desc: t('nasiDus.step2Desc') },
              { step: 3, icon: (
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ), title: t('nasiDus.step3'), desc: t('nasiDus.step3Desc') },
              { step: 4, icon: (
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              ), title: t('nasiDus.step4'), desc: t('nasiDus.step4Desc') },
            ].map((item) => (
              <div key={item.step} className="relative text-center bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-primary-600 text-white text-sm font-bold rounded-full flex items-center justify-center">
                  {item.step}
                </div>
                <div className="w-16 h-16 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* ===== PACKAGES SECTION (Presets) ===== */}
        {packages.length > 0 && (
          <AnimatedSection className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">
              {t('nasiDus.ourPackages')}
            </h2>
            <p className="text-gray-500 text-center mb-10 max-w-xl mx-auto">
              {t('nasiDus.packagePresetsHint') !== 'nasiDus.packagePresetsHint'
                ? t('nasiDus.packagePresetsHint')
                : t('nasiDus.step1Desc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPackages.map((pkg, index) => (
                <AnimatedPackageCard
                  key={pkg.id}
                  pkg={pkg}
                  lang={lang}
                  onSelect={selectPackagePreset}
                  isSelected={selectedPackageId === pkg.id}
                  t={t}
                  index={index}
                />
              ))}
            </div>
          </AnimatedSection>
        )}

        {/* ===== ORDER FORM SECTION ===== */}
        <div ref={formRef} className="scroll-mt-24 mb-16">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {t('nasiDus.orderForm')}
                </h2>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 space-y-6">

                {/* Customer Info */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 border-b pb-2 text-sm uppercase tracking-wide">
                    {t('nasiDus.customerName')}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.customerName')} *</label>
                      <input
                        type="text"
                        {...register('customer_name', { required: true })}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${errors.customer_name ? 'border-red-500' : 'border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.phone')} *</label>
                      <input
                        type="tel"
                        {...register('customer_phone', { required: true })}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${errors.customer_phone ? 'border-red-500' : 'border-gray-300'}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.email')}</label>
                      <input
                        type="email"
                        {...register('customer_email')}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.company')}</label>
                      <input
                        type="text"
                        {...register('customer_company')}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.eventName')}</label>
                    <input
                      type="text"
                      {...register('event_name')}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
                    />
                  </div>
                </div>

                {/* Order Details */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 border-b pb-2 text-sm uppercase tracking-wide">
                    {t('nasiDus.orderForm')}
                  </h3>

                  {/* Branch */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.selectBranch')} *</label>
                    <select
                      {...register('branch_id', { required: true })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${errors.branch_id ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">{t('nasiDus.selectBranch')}...</option>
                      {sortedBranches.map((b) => (
                        <option key={b.branch_id} value={b.branch_id}>
                          {b.name}{b.distance != null && b.distance !== Infinity ? ` (${b.distance.toFixed(1)} km)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Custom order toggle */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_custom"
                      {...register('is_custom')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="is_custom" className="text-sm font-medium text-gray-700">{t('nasiDus.customOrder')}</label>
                  </div>

                  {watchIsCustom && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">{t('nasiDus.customOrderNote')}</p>
                    </div>
                  )}

                  {/* Menu Builder (only if not custom) */}
                  {!watchIsCustom && (
                    <div className="space-y-4">
                      {/* Box Summary */}
                      {Object.keys(boxItems).length > 0 && (
                        <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-primary-900 text-sm uppercase tracking-wide">
                              {t('nasiDus.boxSummary') !== 'nasiDus.boxSummary' ? t('nasiDus.boxSummary') : 'Your Box'}
                            </h4>
                            <button
                              type="button"
                              onClick={() => { setBoxItems({}); setSelectedPackageId(null); }}
                              className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                            >
                              {t('nasiDus.clearBox') !== 'nasiDus.clearBox' ? t('nasiDus.clearBox') : 'Clear'}
                            </button>
                          </div>
                          <ul className="space-y-1.5">
                            {Object.entries(boxItems).map(([itemId, qty]) => {
                              const mi = menuItems.find((m) => m.id === itemId);
                              if (!mi) return null;
                              return (
                                <li key={itemId} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-800">
                                    {qty}x {getLocalizedField(mi, 'name', lang)}
                                  </span>
                                  <span className="text-gray-600 font-medium">{formatPrice(mi.price * qty)}</span>
                                </li>
                              );
                            })}
                          </ul>
                          <div className="mt-3 pt-3 border-t border-primary-200 flex justify-between font-semibold text-primary-800">
                            <span>{t('nasiDus.costPerBox') !== 'nasiDus.costPerBox' ? t('nasiDus.costPerBox') : 'Cost per box'}</span>
                            <span>{formatPrice(boxCost)}</span>
                          </div>
                        </div>
                      )}

                      {/* Menu Item Grid grouped by type */}
                      {Object.keys(menuGroups).length > 0 && (
                        <div className="space-y-4">
                          <p className="text-sm font-medium text-gray-700">
                            {t('nasiDus.buildYourBox') !== 'nasiDus.buildYourBox' ? t('nasiDus.buildYourBox') : 'Build your box - add items below:'}
                          </p>
                          {Object.entries(menuGroups).map(([type, items]) => (
                            <div key={type}>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{type}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {items.map((item) => (
                                  <MenuItemTile
                                    key={item.id}
                                    item={item}
                                    lang={lang}
                                    quantity={boxItems[item.id] || 0}
                                    onAdd={addToBox}
                                    onRemove={removeFromBox}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hidden field for package_id */}
                  <input type="hidden" {...register('package_id')} />

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.quantity')} *</label>
                    <input
                      type="number"
                      min="1"
                      {...register('quantity', { required: true, min: 1 })}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${errors.quantity ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>

                  {/* Price summary */}
                  {!watchIsCustom && Object.keys(boxItems).length > 0 && quantity > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{formatPrice(boxCost)} x {quantity} {t('nasiDus.boxes') !== 'nasiDus.boxes' ? t('nasiDus.boxes') : 'boxes'}</span>
                        <span>{formatPrice(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium text-primary-700 border-t pt-2">
                        <span>{t('nasiDus.depositAmount')} (50%)</span>
                        <span>{formatPrice(depositRequired)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fulfillment */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 border-b pb-2 text-sm uppercase tracking-wide">
                    {t('nasiDus.fulfillment')}
                  </h3>

                  <div className="grid grid-cols-3 gap-3">
                    {['pickup', 'pickup_later', 'delivery'].map((type) => (
                      <label
                        key={type}
                        className={`flex items-center justify-center px-3 py-2.5 border rounded-lg cursor-pointer transition text-sm font-medium text-center ${
                          watchFulfillment === type
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value={type}
                          {...register('fulfillment_type')}
                          className="sr-only"
                        />
                        {t(`nasiDus.${type === 'pickup_later' ? 'pickupLater' : type}`)}
                      </label>
                    ))}
                  </div>

                  {watchFulfillment === 'delivery' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.deliveryAddress')} *</label>
                      <textarea
                        {...register('delivery_address', { required: watchFulfillment === 'delivery' })}
                        rows={2}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${errors.delivery_address ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('nasiDus.deliveryNote')}</p>
                    </div>
                  )}

                  {/* Date & Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.scheduledDate')} *</label>
                      <input
                        type="date"
                        min={getTomorrowDate()}
                        {...register('scheduled_date', { required: true })}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition ${errors.scheduled_date ? 'border-red-500' : 'border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.scheduledTime')}</label>
                      <input
                        type="time"
                        {...register('scheduled_time')}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.notes')}</label>
                    <textarea
                      {...register('notes')}
                      rows={2}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('nasiDus.specialRequests')}</label>
                    <textarea
                      {...register('special_requests')}
                      rows={2}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
                >
                  {isSubmitting ? t('nasiDus.submitting') : t('nasiDus.submitOrder')}
                </button>
              </form>
            </div>
          </AnimatedSection>
        </div>

        {/* ===== MY ORDERS SECTION ===== */}
        <AnimatedSection className="mb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
              {t('nasiDus.myOrders') !== 'nasiDus.myOrders' ? t('nasiDus.myOrders') : 'My Orders'}
            </h2>

            {!user ? (
              <div className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="text-gray-600">
                  {t('nasiDus.loginToTrack') !== 'nasiDus.loginToTrack' ? t('nasiDus.loginToTrack') : 'Log in to track your orders'}
                </p>
              </div>
            ) : ordersLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
              </div>
            ) : myOrders.length === 0 ? (
              <div className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-gray-600">
                  {t('nasiDus.noOrders') !== 'nasiDus.noOrders' ? t('nasiDus.noOrders') : 'No orders yet'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {myOrders.map((order) => (
                  <OrderCard key={order.id} order={order} t={t} />
                ))}
              </div>
            )}
          </div>
        </AnimatedSection>

        {/* ===== FAQ SECTION ===== */}
        <AnimatedSection className="mb-16">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
              {t('nasiDus.faq')}
            </h2>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <FaqItem
                  key={n}
                  question={t(`nasiDus.faq${n}Q`)}
                  answer={t(`nasiDus.faq${n}A`)}
                  isOpen={openFaq === n}
                  onToggle={() => setOpenFaq(openFaq === n ? null : n)}
                />
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}
